import React, { useState } from 'react';
import { Eye, X, Check } from 'lucide-react';
import ActivitySummary from './ActivitySummary';
import TeamInfoBanner from './TeamInfoBanner';

const OverviewTab = ({ selectedUser, candidates, jobs, submissions, myTeam, teamLead, availableUsers }) => {
  const [selectedIndustryJobs, setSelectedIndustryJobs] = useState(null);
  
  const stats = {
    totalCandidates: candidates.length,
    activeCandidates: candidates.filter(c => c.candidate_status === 'Active').length,
    totalJobs: jobs.length,
    openJobs: jobs.filter(j => (j.status || '').toLowerCase().includes('accepting')).length,
    mySubmissions: submissions.length,
    teamSize: myTeam ? myTeam.team_members?.length || 0 : 0
  };

  return (
    <div className="space-y-6">
      {/* Team Info Banner - Shows for recruiters */}
      <TeamInfoBanner selectedUser={selectedUser} teamLead={teamLead} myTeam={myTeam} />
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h2 className="text-2xl font-bold">Welcome, {selectedUser.full_name}!</h2>
        <p className="text-blue-100 mt-1">
          {selectedUser.role === 'recruiter_supervisor' && 'Supervising recruiting operations'}
          {selectedUser.role === 'lead_recruiter' && `Leading ${myTeam?.team_name || 'your team'} with ${stats.teamSize} member(s)`}
          {selectedUser.role === 'recruiter' && 'Managing your candidate pipeline'}
          {(selectedUser.role === 'account_manager' || selectedUser.role === 'account_exec') && 
            `Managing ${selectedUser.assigned_clients?.length || 0} client ${selectedUser.assigned_clients?.length === 1 ? 'account' : 'accounts'}`}
        </p>
      </div>

      {/* Activity Summary - Replaces Low Coverage section */}
      <ActivitySummary 
        selectedUser={selectedUser}
        submissions={submissions}
        jobs={jobs}
        candidates={candidates}
        teamLead={teamLead}
        team={myTeam}
        allUsers={availableUsers}
      />
      
      {/* Selected Industry Jobs Display - ONLY JOBS WITH NO SUBMISSIONS */}
      {selectedIndustryJobs && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-lg p-6">
          {/* Big Alert Badge Above Everything */}
          <div className="flex items-center justify-between mb-6">
            <div></div> {/* Spacer */}
            <span className="bg-red-600 text-white px-6 py-3 rounded-lg text-xl font-bold shadow-lg animate-pulse">
              ⚠️ NEEDS ATTENTION!!!
            </span>
            <button
              onClick={() => setSelectedIndustryJobs(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">
                {selectedIndustryJobs.industry} Jobs - No Submissions Yet
              </h3>
              <div className="mt-2 bg-yellow-100 border border-yellow-300 rounded-lg p-3">
                <p className="text-sm font-medium text-yellow-900">
                  📋 What you're seeing: {selectedIndustryJobs.jobs.length} jobs with NO submissions
                </p>
                <p className="text-xs text-yellow-800 mt-1">
                  {selectedIndustryJobs.ageFilter <= 3 
                    ? `These jobs are ${selectedIndustryJobs.ageFilter}+ days old but haven't received any candidate submissions yet. Time to start sourcing!`
                    : selectedIndustryJobs.ageFilter <= 7
                    ? `These jobs are ${selectedIndustryJobs.ageFilter}+ days old with zero submissions. Follow up with hiring manager and submit candidates immediately.`
                    : selectedIndustryJobs.ageFilter <= 15
                    ? `🔥 These jobs are ${selectedIndustryJobs.ageFilter}+ days old with NO submissions. This is getting stale - urgent action required!`
                    : selectedIndustryJobs.ageFilter <= 30
                    ? `🚨 CRITICAL: These jobs are ${selectedIndustryJobs.ageFilter}+ days old with ZERO submissions. Client may be frustrated. Submit candidates NOW!`
                    : `🚨🚨 VERY CRITICAL: These jobs are ${selectedIndustryJobs.ageFilter}+ days old with NO submissions. Risk of losing client. Immediate action required!`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {selectedIndustryJobs.jobs.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <Check className="h-12 w-12 text-green-600 mx-auto mb-3" />
              <p className="text-green-800 font-medium">✓ Great job!</p>
              <p className="text-sm text-green-700 mt-2">
                All {selectedIndustryJobs.priority} priority jobs in {selectedIndustryJobs.industry} have submissions.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedIndustryJobs.jobs.map(job => {
                const createdDate = job.created_at ? new Date(job.created_at) : new Date();
                const daysOld = Math.floor((new Date() - createdDate) / (1000 * 60 * 60 * 24));
                
                return (
                  <div key={job.id} className="bg-white border-2 border-orange-300 rounded-lg p-4 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{job.title}</h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            job.priority === 'high' ? 'bg-red-100 text-red-800' :
                            job.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {job.priority}
                          </span>
                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold">
                            0 submissions
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{job.client}</p>
                        <p className="text-xs text-gray-500 mt-1">{job.location}</p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
                          <span>${(job.salary_range_min / 1000).toFixed(0)}K - ${(job.salary_range_max / 1000).toFixed(0)}K</span>
                          <span>{job.no_of_openings || 1} opening(s)</span>
                          <span className={`px-2 py-1 rounded font-medium ${
                            daysOld <= 3 ? 'bg-yellow-100 text-yellow-800' :
                            daysOld <= 7 ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {daysOld} days old
                          </span>
                        </div>
                      </div>
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
